import React, { useContext, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import { SidebarFlyoutContext } from './SidebarFlyoutContext';
import SidebarIcon from './SidebarIcon';

export interface FlyoutItem {
  to?: string;
  href?: string;
  label: string;
  parent?: boolean;
}

interface IProps {
  flyoutKey: string;
  label: string;
  icon?: string;
  iconSrc?: string;
  to?: string;
  href?: string;
  end?: boolean;
  active?: boolean;
  header?: string;
  items: FlyoutItem[];
}

export default function SidebarFlyout({ flyoutKey, label, icon, iconSrc, to, href, end, active, header, items }: IProps) {
  const { openKey, open, closeSoon, close } = useContext(SidebarFlyoutContext);
  const host = useRef<HTMLLIElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>();
  const isOpen = openKey === flyoutKey;

  useLayoutEffect(() => {
    if (!isOpen) return;

    const trackHost = () => {
      if (!host.current) return;
      const rect = host.current.getBoundingClientRect();
      setPosition({ top: rect.top, left: rect.right });
    };

    trackHost();
    window.addEventListener('resize', trackHost);
    document.addEventListener('scroll', trackHost, true);
    return () => {
      window.removeEventListener('resize', trackHost);
      document.removeEventListener('scroll', trackHost, true);
    };
  }, [isOpen]);

  const triggerClass = `nav-link rounded-2 mx-1 px-0 justify-content-center${active ? ' active' : ''}`;
  const iconElement = <SidebarIcon icon={icon} iconSrc={iconSrc} />;

  const trigger = href
    ? <a className={triggerClass} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} onClick={close}>
      {iconElement}
    </a>
    : to
      ? <NavLink className={triggerClass} to={to} end={end} aria-label={label} onClick={close}>
        {iconElement}
      </NavLink>
      : <span className={triggerClass} role="button" tabIndex={0} aria-label={label}>
        {iconElement}
      </span>;

  return (
    <li
      ref={host}
      className="nav-item"
      style={{ marginBottom: '0.4rem' }}
      onMouseEnter={() => open(flyoutKey)}
      onMouseLeave={closeSoon}>
      {trigger}
      {isOpen && createPortal(
        <div
          data-testid="sidebar-flyout"
          className="sidebar-flyout position-fixed bg-white border rounded-3 shadow py-2 ms-2"
          style={{ top: position?.top, left: position?.left, minWidth: '10rem', zIndex: 1000 }}
          onMouseEnter={() => open(flyoutKey)}
          onMouseLeave={closeSoon}>
          {header &&
            <div className="text-uppercase text-muted fw-semibold px-3 pb-1" style={{ fontSize: '0.75rem', letterSpacing: '0.04em' }}>
              {header}
            </div>}
          {items.map(item =>
            item.href
              ? <a
                key={item.href}
                className="sidebar-flyout-item"
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={close}>
                {item.label}
                <i className="bi-box-arrow-up-right small text-muted ms-2"></i>
              </a>
              : <NavLink
                key={item.to}
                className={`sidebar-flyout-item${item.parent ? ' fw-semibold border-bottom mb-1 pb-2' : ''}`}
                to={item.to ?? ''}
                end={item.to === '/'}
                onClick={close}>
                {item.label}
              </NavLink>)}
        </div>,
        document.body)}
    </li>
  );
}
